import {
  fiscalYearOf,
  NEPAL_FISCAL_YEAR,
  type FiscalYearSettings,
  type NumberingInput,
} from '@ims/core'
import type { LocalRepository } from '@ims/local'
import type { NumberedDoc, TenantSettingsRow } from '@ims/data'

export function fiscalSettings(settings: TenantSettingsRow | null | undefined): FiscalYearSettings {
  if (!settings) return NEPAL_FISCAL_YEAR
  return {
    calendar: settings.fiscal_calendar,
    startMonth: settings.fiscal_year_start_month,
    startDay: settings.fiscal_year_start_day,
  }
}

export interface Numbering extends NumberingInput {
  /** True when the server assigns the number on sync (WEB counter). */
  serverNumbered: boolean
}

/**
 * Numbering for a new finalized document. This device's own billing counter
 * (numbers offline) when registered, otherwise the WEB counter (the server
 * numbers it when the document syncs).
 */
export async function numberingFor(
  repo: LocalRepository,
  settings: TenantSettingsRow,
  docType: NumberedDoc,
  dateIso: string,
): Promise<Numbering> {
  const fiscalYear = fiscalYearOf(dateIso, fiscalSettings(settings))
  const prefix = {
    sales_invoice: settings.invoice_prefix,
    credit_note: settings.credit_note_prefix,
    quotation: settings.quotation_prefix,
    sales_order: settings.sales_order_prefix,
    purchase_order: settings.purchase_order_prefix,
    purchase_request: settings.purchase_request_prefix,
  }[docType]
  const device = await repo.counters.current()
  if (device && device.is_active && device.kind === 'device') {
    const seq = await repo.counters.nextSeq(docType, fiscalYear)
    return {
      counter: { id: device.id, code: device.code, kind: 'device' },
      prefix,
      fiscalYear,
      seq,
      serverNumbered: false,
    }
  }
  const web = (await repo.list('billing_counters', { where: { kind: 'web' }, limit: 1 })).rows[0]
  if (!web) throw new Error('no_counter: sync once while online to load billing counters')
  return {
    counter: { id: web.id, code: web.code, kind: 'web' },
    prefix,
    fiscalYear,
    serverNumbered: true,
  }
}
